import { useEffect, useState } from "react";
import { ExtendedCard, FormAlert } from "@NP-Webber/crm-kit";
import { formatShekels } from "../../api/financeApi";
import {
  nedarimApi,
  type NedarimMovement,
  type SubscriptionWithPeriod,
} from "../../api/nedarimApi";
import { toClientApiError } from "../../api/httpClient";

const STATUS_LABELS: Record<string, string> = {
  active: "פעיל",
  paused: "מושהה",
  draft: "טיוטה",
  cancelled: "בוטל",
  expired: "פג",
};

const PERIOD_LABELS: Record<string, string> = {
  paid: "שולם",
  unpaid: "לא שולם",
  failed: "נכשל",
  none: "ללא הו״ק",
};

const KIND_LABELS: Record<string, string> = {
  transaction: "עסקה",
  mandate: "הו״ק",
  charge: "חיוב",
  siruv: "סירוב",
};

const MOVEMENT_STATUS_LABELS: Record<string, string> = {
  success: "הצלחה",
  failed: "נכשל",
  pending: "ממתין",
  cancelled: "בוטל",
};

export type SubscriptionCardProps = {
  subscription: SubscriptionWithPeriod;
  customerName: string;
  categoryName: string;
  onClose: () => void;
};

function channelLabel(value: string | null): string {
  if (value === "credit") return "אשראי";
  if (value === "bank") return "מס״ב";
  return "—";
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("he-IL");
}

export function SubscriptionCard({
  subscription,
  customerName,
  categoryName,
  onClose,
}: SubscriptionCardProps) {
  const [movements, setMovements] = useState<NedarimMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void nedarimApi
      .listMovements({ subscriptionId: subscription.id })
      .then((items) => {
        if (!cancelled) setMovements(items);
      })
      .catch((err) => {
        if (!cancelled) setError(toClientApiError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subscription.id]);

  const detailsFields = [
    { key: "customer", label: "קופה", value: customerName },
    { key: "category", label: "סוג מנוי", value: categoryName || "—" },
    {
      key: "amount",
      label: "תעריף",
      value: `${formatShekels(subscription.amountAgorot)} ₪`,
    },
    {
      key: "status",
      label: "סטטוס",
      value: STATUS_LABELS[subscription.status] ?? subscription.status,
    },
    {
      key: "periodCharge",
      label: `גבייה ${subscription.billingPeriod}`,
      value: PERIOD_LABELS[subscription.periodChargeStatus] ?? subscription.periodChargeStatus,
    },
    {
      key: "paymentArrangement",
      label: "הסדר תשלום",
      value: subscription.paymentArrangement?.trim() || "ללא הסדר",
    },
    {
      key: "channel",
      label: "ערוץ נדרים",
      value: channelLabel(subscription.nedarimChannel),
    },
    {
      key: "startsOn",
      label: "תחילה",
      value: formatDate(subscription.startsOn),
    },
    {
      key: "endsOn",
      label: "סיום",
      value: formatDate(subscription.endsOn),
    },
  ];

  const historyContent = (
    <div className="related-panel">
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      {loading ? <p className="related-panel__empty">טוען היסטוריה…</p> : null}
      {!loading && !error && movements.length === 0 ? (
        <p className="related-panel__empty">אין תנועות תשלום למנוי זה.</p>
      ) : null}
      {!loading && movements.length > 0 ? (
        <ul className="related-panel__list">
          {movements.map((m) => (
            <li key={m.id} className="related-panel__item">
              <strong>
                {formatDate(m.occurredOn)} · {KIND_LABELS[m.kind] ?? m.kind}
              </strong>
              <span>
                {[
                  m.billingPeriod || null,
                  `${formatShekels(m.amountAgorot)} ₪`,
                  MOVEMENT_STATUS_LABELS[m.status] ?? m.status,
                  channelLabel(m.channel),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );

  return (
    <ExtendedCard
      open
      onClose={onClose}
      title={subscription.title}
      subtitle={`${customerName} · ${STATUS_LABELS[subscription.status] ?? subscription.status}`}
      detailsFields={detailsFields}
      feedSources={{ activities: [], tasks: [], notes: [] }}
      hideFeedTab
      showDashboard={false}
      defaultTab={0}
      extraTabs={[
        {
          label: "היסטוריית תשלומים",
          content: historyContent,
        },
      ]}
    />
  );
}
